<template>
  <div class="app-body" :class="{ mobile: isNarrowWidthDevice }">
    <apnic-nav-bar :isMobile="isNarrowWidthDevice"></apnic-nav-bar>
    <div id="main-wrapper" class="navigation">
      <div
        v-show="
          (!isNarrowWidthDevice && !isWelcomePage) || isShowSidebarOnMobile
        "
        class="sidebar"
        :class="{ compact: !isExpandSidebar }"
      >
        <apnic-side-bar
          :isMobile="isNarrowWidthDevice"
          @onToggleIsShowSidebarOnMobile="toggleIsShowSidebarOnMobile"
        ></apnic-side-bar>
      </div>

      <main
        v-show="!isNarrowWidthDevice || !isShowSidebarOnMobile"
        id="page-content-wrapper"
      >
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import NavBar from './components/ui/NavBar.vue';
import SideBar from './components/ui/SideBar.vue';
import { getScrollY } from './utils/BOM';
import languageConfig from './config/language';

const SIDEBAR_EXPANDED_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const OMNIBAR_HEIGHT = 40;

export default {
  methods: {
    ...mapActions('authStore', ['authenticateOidcSilent']),
    ...mapMutations(['setIsShowSidebarOnMobile']),
    ...mapActions(['fetchThemeDataList']),

    handleSideBar() {
      const width = window.innerWidth;

      if (
        (width < 768 && this.showSideBar) ||
        (width > 768 && !this.showSideBar)
      ) {
        this.$store.dispatch('toggleSideBar');
      }

      // window.orientation = 90 / -90 when it is on Mobile device and in landscape mode
      // window.orientation = 0 / 180 when it is on Mobile device and in portrait mode
      // window.orientation = undefined when it is on non-Mobile device
      // For some browsers, screen.orientation is used to replace window.orientation. However, screen is not supported by all browsers yet
      const isMobileDeviceInLandscapeMode =
        window.orientation === 90 ||
        window.orientation === -90 ||
        (screen && screen.orientation && screen.orientation.angle === 90) ||
        (screen && screen.orientation && screen.orientation.angle === -90);

      if (width < 768 && !this.isNarrowWidthDevice)
        this.$store.commit('setIsNarrowWidthDevice', true);

      if (
        width >= 768 &&
        !isMobileDeviceInLandscapeMode &&
        this.isNarrowWidthDevice
      )
        this.$store.commit('setIsNarrowWidthDevice', false);

      if (width > 768 && !this.isWelcomePage)
        this.resolveStickyConflictWithOverflow();

      if (width >= 992 && !this.isLargeWidthDevice)
        this.$store.commit('setIsLargeWidthDevice', true);

      if (width < 992 && this.isLargeWidthDevice)
        this.$store.commit('setIsLargeWidthDevice', false);

      if (width >= 1200 && !this.isExtraLargeWidthDevice)
        this.$store.commit('setIsExtraLargeWidthDevice', true);

      if (width < 1200 && this.isExtraLargeWidthDevice)
        this.$store.commit('setIsExtraLargeWidthDevice', false);
    },

    handleOrientationChange() {
      const isComparisonPage =
        window.location.pathname.includes('/apnic-comparison');
      if (isComparisonPage) window.location.reload();
    },

    toggleIsShowSidebarOnMobile() {
      this.setIsShowSidebarOnMobile(false);
    },

    resolveStickyConflictWithOverflow() {
      const elementPageContentWrapper = document.getElementById(
        'page-content-wrapper'
      );
      const elementSubheader = document.querySelector('.fixed-header');

      if (!elementPageContentWrapper || !elementSubheader) return;

      elementPageContentWrapper.style['overflow-x'] = 'hidden';

      elementSubheader.style.top = getScrollY() + 'px';

      clearTimeout(this.timer);

      this.timer = setTimeout(() => {
        elementPageContentWrapper.style['overflow-x'] = 'visible';
        const width =
          window.innerWidth - this.isExpandSidebar
            ? SIDEBAR_EXPANDED_WIDTH
            : SIDEBAR_COLLAPSED_WIDTH;
        elementPageContentWrapper.style['width'] = width + 'px';
        elementSubheader.style.top = OMNIBAR_HEIGHT + 'px';
      }, 400);
    },
  },
  computed: {
    ...mapState([
      'isNarrowWidthDevice',
      'isExpandSidebar',
      'isShowSidebarOnMobile',
      'isLargeWidthDevice',
      'isExtraLargeWidthDevice',
    ]),
    showSideBar() {
      return this.$store.getters.showSideBar;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.isWelcomePage = to.path === '/' || to.path === '/auth-callback';

        if (this.isShowSidebarOnMobile && this.isNarrowWidthDevice) {
          this.setIsShowSidebarOnMobile(false);
        }
      },
    },
  },
  data() {
    return {
      isWelcomePage: true,
    };
  },
  components: {
    apnicNavBar: NavBar,
    apnicSideBar: SideBar,
  },
  mounted() {
    window.addEventListener('resize', this.handleSideBar);
    window.addEventListener('orientationchange', this.handleOrientationChange);
    this.handleSideBar();

    // Trigger silent login
    this.authenticateOidcSilent()
      .then(() => {})
      .catch(() => {});

    // Trigger Event for GTM to track the browser default language
    window.dataLayer.push({
      event: 'browser-language',
      browserLanguage: window.navigator.language,
    });

    // Get selected language from localStorage
    const selectedLanguage = languageConfig.getSelectedLanguageInStorage();

    // Trigger Event for GTM to track the selected language if it exists.
    if (selectedLanguage) {
      window.dataLayer.push({
        event: 'selected-language',
        selectedLanguage: selectedLanguage.locale,
      });
    }

    this.fetchThemeDataList();
  },
};
</script>

<style lang="scss">
.navigation {
  position: relative;
  top: 40px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;

  .sidebar {
    flex: 0 0 300px;
    transition: flex 0.5s;

    &.compact {
      flex: 0 0 64px;
    }
  }
}
// fix footer position when content is not long enought, martyna is aware of this, so should be fixed in later release
main#page-content-wrapper {
  flex: 1;
  position: relative;
  width: 100%;
  min-height: calc(100vh - 147px);
  overflow-x: visible;
}

.ui-dialog.ui-widget.stat-maximize-modal {
  z-index: 9999 !important;
}
.hide-sidebar#wrapper {
  padding-left: 0 !important;
}
.mobile-modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba($color: #1a1a1a, $alpha: 0.7);
  z-index: 99999;

  &__card {
    width: 300px !important;
    padding: 20px;
    background: white;
  }
  &__header {
    margin-bottom: 40px;
  }
  &__body {
    margin-bottom: 20px;
  }
  &__footer {
    text-align: right;
  }

  h1 {
    margin-bottom: 0;
    font-size: 30px;
  }

  p {
    font-size: 1rem;
  }

  .btn.btn-close {
    width: 71px;
    min-width: 71px;
  }
}

body.app-body {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

.app-body {
  margin: 0;
  padding: 0;
}

.mobile {
  .navigation {
    top: 0;
  }
}

div.usabilla_live_button_container {
  z-index: 1 !important;
}
</style>
